<template>
  <div class="cart-drawer__root" role="presentation">
    <div class="cart-drawer__overlay" @click="$emit('close')" aria-hidden="true"></div>
    <aside
      class="cart-drawer"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
    >
      <header class="cart-drawer__header">
        <h2>{{ title }}</h2>
        <button type="button" class="cart-drawer__close" @click="$emit('close')">
          Close
        </button>
      </header>

      <div v-if="items.length" class="cart-drawer__items">
        <div
          v-for="(item, index) in items"
          :key="itemKey(item, index)"
          class="cart-drawer__item"
        >
          <div class="cart-drawer__item-info">
            <span class="cart-drawer__item-name">{{ getItemName(item) }}</span>
            <span class="cart-drawer__item-price">
              {{ currencyPrefix }}{{ displayPrice(getItemPrice(item)) }}
            </span>
          </div>
          <div class="cart-drawer__controls">
            <button
              type="button"
              @click="$emit('decrease', index)"
              aria-label="Decrease quantity"
            >
              -
            </button>
            <span class="cart-drawer__quantity">{{ item.quantity }}</span>
            <button
              type="button"
              @click="$emit('increase', index)"
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
          <button
            type="button"
            class="cart-drawer__remove"
            @click="$emit('remove', index)"
          >
            Remove
          </button>
        </div>
      </div>
      <div v-else class="cart-drawer__empty">
        <slot name="empty">Your cart is currently empty.</slot>
      </div>

      <footer class="cart-drawer__footer">
        <div class="cart-drawer__summary">
          <span>{{ totalLabel }}</span>
          <span>{{ currencyPrefix }}{{ displayPrice(total) }}</span>
        </div>
        <button
          type="button"
          class="cart-drawer__checkout"
          :disabled="!items.length"
          @click="$emit('checkout')"
        >
          {{ checkoutLabel }}
        </button>
      </footer>
    </aside>
  </div>
</template>

<script>
export default {
  name: 'CartDrawer',
  props: {
    title: {
      type: String,
      default: 'Shopping Cart'
    },
    items: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    totalLabel: {
      type: String,
      default: 'Total'
    },
    checkoutLabel: {
      type: String,
      default: 'Checkout'
    },
    formatPrice: {
      type: Function,
      default: value => {
        if (typeof value === 'number') {
          return value.toLocaleString('en-ZA')
        }
        const numeric = parseFloat(String(value).replace(/[^\d.]/g, ''))
        return Number.isNaN(numeric) ? '0' : numeric.toLocaleString('en-ZA')
      }
    },
    getItemPrice: {
      type: Function,
      default: item => item?.price || 0
    },
    getItemName: {
      type: Function,
      default: item => item?.product || ''
    },
    currencyPrefix: {
      type: String,
      default: 'R'
    }
  },
  emits: ['close', 'increase', 'decrease', 'remove', 'checkout'],
  methods: {
    itemKey(item, index) {
      const key = item?.product || item?.name
      return key ? `${key}-${index}` : index
    },
    displayPrice(value) {
      return this.formatPrice(value)
    }
  }
}
</script>

<style scoped>
.cart-drawer__root {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  justify-content: flex-end;
}

.cart-drawer__overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 29, 18, 0.45);
  backdrop-filter: blur(2px);
}

.cart-drawer {
  position: relative;
  height: 100vh;
  width: min(380px, 92vw);
  background: #ffffff;
  box-shadow: -16px 0 40px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
}

.cart-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid rgba(15, 29, 18, 0.08);
}

.cart-drawer__header h2 {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f1d12;
}

.cart-drawer__close {
  background: transparent;
  border: none;
  font-size: 0.9rem;
  color: #4b5563;
  cursor: pointer;
}

.cart-drawer__items {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cart-drawer__item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  align-items: center;
}

.cart-drawer__item-info {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.cart-drawer__item-name {
  font-weight: 600;
  color: #0f1d12;
}

.cart-drawer__item-price {
  font-size: 0.9rem;
  color: #4b5563;
}

.cart-drawer__controls {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(82, 205, 54, 0.12);
  border-radius: 9999px;
  padding: 0.25rem 0.75rem;
}

.cart-drawer__controls button {
  background: transparent;
  border: none;
  font-size: 1rem;
  color: #1f3016;
  cursor: pointer;
}

.cart-drawer__quantity {
  min-width: 1.5rem;
  text-align: center;
  font-weight: 600;
}

.cart-drawer__remove {
  justify-self: end;
  background: transparent;
  border: none;
  font-size: 0.85rem;
  color: #dc2626;
  cursor: pointer;
}

.cart-drawer__empty {
  padding: 2.5rem 1.75rem;
  text-align: center;
  color: #4b5563;
}

.cart-drawer__footer {
  padding: 1.5rem 1.75rem;
  border-top: 1px solid rgba(15, 29, 18, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cart-drawer__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: #0f1d12;
}

.cart-drawer__checkout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1.25rem;
  border-radius: 9999px;
  background: linear-gradient(135deg, #52cd36, #3da026);
  color: #ffffff;
  border: none;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cart-drawer__checkout:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cart-drawer__checkout:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px rgba(82, 205, 54, 0.28);
}
@media (max-width: 640px) {
  .cart-drawer {
    width: min(340px, 92vw);
  }
}
</style>









